import React from "react";
import fp from "lodash/fp";
import { useDispatch, useSelector } from "react-redux";
import {
	Field,
	getFormValues,
	InjectedFormProps,
	isDirty,
	reduxForm,
	stopSubmit,
} from "redux-form";

import { Button } from "components";
import {
	CustomLoginSteps,
	EmailVerificationFormName,
	LoginFormName,
	MaxAttempts,
} from "globalConstants";
import { getErrorMessage } from "helpers";
import { userActions } from "store";
import {
	emailValid,
	emailWithDomainsValid,
	trimMidSentenceNormalizer,
	trimNormalizer,
} from "validators";

import FormInputGroup from "./formInputGroup";

import styles from "./login.module.scss";

type EmailVerificationFormProps = {
	handleSubmit: Function;
	submitting: boolean;
	handleLogin: (value: any) => any;
	setStep: Function;
	attempts: number;
	domains: string[];
};

const EmailVerificationForm = ({
	handleSubmit,
	submitting,
	handleLogin,
	setStep,
	attempts,
	domains,
}: InjectedFormProps<any, EmailVerificationFormProps> &
	EmailVerificationFormProps) => {
	const dispatch = useDispatch<any>();

	const { UsernameStep } = CustomLoginSteps;

	const isDirtyForm: boolean = useSelector(
		isDirty(EmailVerificationFormName),
	);
	const password = useSelector(
		fp.flow(getFormValues(LoginFormName), fp.get("password")),
	);
	const username = useSelector(
		fp.flow(getFormValues(LoginFormName), fp.get("username")),
	);

	const dispatchLogin = (email: string | undefined) => {
		dispatch(userActions.login(username, password, email))
			.then(handleLogin)
			.catch(error => {
				return dispatch(
					stopSubmit(EmailVerificationFormName, {
						email: getErrorMessage(error?.response?.data),
					}),
				);
			});
	};

	const handleSubmitEmailVerification = ({ email }) => {
		if (email) {
			return dispatchLogin(email);
		}
	};

	const handleSkipEmailVerification = () => {
		return dispatchLogin(undefined);
	};

	const handleLogout = () => {
		return setStep(UsernameStep);
	};

	const handleEmailWithDomainsValid = value => {
		return emailWithDomainsValid(value, domains);
	};

	return (
		<form onSubmit={handleSubmit(handleSubmitEmailVerification)}>
			<Field
				component={FormInputGroup}
				label="Email"
				name="email"
				id="email"
				placeholder="Enter your Email"
				autoFocus
				validate={[emailValid, handleEmailWithDomainsValid]}
				normalize={trimMidSentenceNormalizer}
				normalizeOnBlur={trimNormalizer}
			/>
			<div className={styles.buttonsHolder}>
				{attempts === MaxAttempts ? (
					<Button
						disabled={attempts !== MaxAttempts || submitting}
						value="Logout"
						onClick={handleLogout}
					/>
				) : (
					<Button
						disabled={attempts === MaxAttempts || submitting}
						value={`Skip: ${MaxAttempts - attempts} Attempts`}
						variant="outline-primary"
						onClick={handleSkipEmailVerification}
					/>
				)}
				<Button
					type="submit"
					disabled={!isDirtyForm || submitting}
					value="Continue"
				/>
			</div>
		</form>
	);
};

export default reduxForm<any, EmailVerificationFormProps>({
	form: EmailVerificationFormName,
	destroyOnUnmount: false,
	forceUnregisterOnUnmount: true,
})(EmailVerificationForm);
